// External imports
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { darken } from '@mui/material/styles';
import useTheme from '@mui/system/useTheme';
import React from 'react';

// Internal imports
import { CircularProgress } from './CircularProgress';

// Declare module augmentation for ButtonPropsColorOverrides
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    alert: true;
  }
}

interface CustomButtonProps {
  loading?: boolean;
}

export function Button<C extends React.ElementType = 'button'>(
  props: MuiButtonProps<C, { component?: C } & CustomButtonProps>
) {
  const {
    variant = 'text',
    color = 'primary',
    sx,
    loading,
    children,
    ...otherProps
  } = props;
  const theme = useTheme();

  const isFilled = variant === 'contained';
  const isPrimary = color === 'primary';
  const isAlert = color === 'alert';

  // We use darken on these colors for now so we cant use css vars
  const primaryColor = theme.palette.primary.ogSmart;
  const alertColor = theme.palette.alert.main;

  const styles = [
    { transition: 'all 0.3s' },
    isFilled &&
      isPrimary && {
        bgcolor: 'primary.ogSmart',
        backgroundImage: `linear-gradient(180deg, ${primaryColor} 0%, ${darken(
          primaryColor,
          0.15
        )} 100%)`,
        color: 'primary.ogSmartContrastText',
      },
    isFilled &&
      isAlert && {
        bgcolor: 'alert.main',
        backgroundImage: `linear-gradient(180deg, ${alertColor} 0%, ${darken(
          alertColor,
          0.15
        )} 100%)`,
        color: '#FFFFFF', // Explicit white color
      },
    !isFilled &&
      isPrimary && {
        color: 'primary.ogSmart',
        borderColor: 'primary.ogSmart',
      },
    !isFilled &&
      isAlert && {
        color: 'alert.main',
        borderColor: 'alert.main',
      },
  ];

  return (
    <MuiButton
      sx={[...styles, ...(Array.isArray(sx) ? sx : [sx])]}
      variant={variant}
      color={isAlert ? 'alert' : color}
      {...otherProps}
    >
      {loading ? (
        <CircularProgress color="inherit" size="small" sx={{ p: '3px' }} />
      ) : (
        children
      )}
    </MuiButton>
  );
}

type ButtonProps = MuiButtonProps & CustomButtonProps;

export type { ButtonProps };
